import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Scale, Loader2, ArrowRight } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { formatApiErrorDetail } from "@/lib/api";

const BG = "https://images.unsplash.com/photo-1764410481612-7544525b2991?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NTYxODh8MHwxfHNlYXJjaHwyfHxsdXh1cnklMjBvZmZpY2UlMjBkZXNrfGVufDB8fHx8MTc4MjA2Mjg0NXww&ixlib=rb-4.1.0&q=85";

export default function Login() {
  const { login } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      await login(email, password);
      navigate("/");
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen relative flex items-center justify-center p-5 overflow-hidden bg-ink">
      <img src={BG} alt="" className="absolute inset-0 w-full h-full object-cover" />
      <div className="absolute inset-0 bg-gradient-to-br from-ink/90 via-ink/75 to-ink/95" />
      <div className="absolute inset-0" style={{ backgroundImage: "radial-gradient(circle at 30% 30%, rgba(181,145,78,0.15), transparent 45%)" }} />

      <div className="relative w-full max-w-5xl grid lg:grid-cols-2 rounded-2xl overflow-hidden border border-white/10 card-elev animate-fade-up">
        {/* Brand side */}
        <div className="hidden lg:flex flex-col justify-between p-12 glass">
          <div className="flex items-center gap-3">
            <div className="w-11 h-11 rounded-lg flex items-center justify-center bg-gradient-to-br from-gold to-gold-dark text-ink">
              <Scale className="w-6 h-6" strokeWidth={1.75} />
            </div>
            <div className="text-white font-display text-2xl font-semibold tracking-wide">Arsip Notaris</div>
          </div>

          <div>
            <div className="text-[11px] uppercase tracking-[0.32em] text-gold mb-5">Digitalisasi Arsip</div>
            <h1 className="font-display text-5xl font-light leading-[1.08] text-white">
              Setiap akta,<br />satu pencarian.
            </h1>
            <p className="text-white/65 mt-6 text-[15px] leading-relaxed max-w-sm">
              Temukan dokumen klien dalam hitungan detik — tanpa membuka lemari fisik satu per satu.
            </p>
          </div>

          <div className="flex items-center gap-6 text-white/50 text-xs">
            <span className="flex items-center gap-2"><span className="w-1.5 h-1.5 rounded-full bg-gold" /> Aman & Terenkripsi</span>
            <span className="flex items-center gap-2"><span className="w-1.5 h-1.5 rounded-full bg-gold" /> OCR Otomatis</span>
          </div>
        </div>

        {/* Form side */}
        <div className="bg-white p-8 sm:p-12 flex items-center">
          <form onSubmit={handleSubmit} className="w-full" data-testid="login-form">
            <div className="lg:hidden flex items-center gap-3 mb-8">
              <div className="w-10 h-10 rounded-lg flex items-center justify-center bg-gradient-to-br from-gold to-gold-dark text-ink">
                <Scale className="w-5 h-5" strokeWidth={1.75} />
              </div>
              <div className="font-display text-xl font-semibold">Arsip Notaris</div>
            </div>

            <div className="text-[11px] uppercase tracking-[0.28em] text-gold mb-3">Selamat Datang</div>
            <h2 className="font-display text-4xl font-medium mb-2 text-ink">Masuk ke Brankas</h2>
            <p className="text-sm text-[#606B75] mb-8">Akses arsip dokumen digital Anda.</p>

            {error && (
              <div className="mb-5 px-4 py-3 bg-wine/5 border border-wine/20 text-wine text-sm rounded-lg" data-testid="login-error">
                {error}
              </div>
            )}

            <label className="block text-xs font-medium uppercase tracking-[0.14em] text-[#606B75] mb-2">Email</label>
            <input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              data-testid="email-input"
              className="w-full mb-5 px-4 py-3 bg-linen border border-borderc rounded-lg text-sm outline-none focus:border-gold focus:ring-1 focus:ring-gold transition"
              required
            />

            <label className="block text-xs font-medium uppercase tracking-[0.14em] text-[#606B75] mb-2">Kata Sandi</label>
            <input
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              data-testid="password-input"
              className="w-full mb-8 px-4 py-3 bg-linen border border-borderc rounded-lg text-sm outline-none focus:border-gold focus:ring-1 focus:ring-gold transition"
              required
            />

            <button
              type="submit"
              disabled={loading}
              data-testid="login-submit-btn"
              className="group w-full bg-ink text-white py-3.5 rounded-lg text-sm font-medium hover:bg-ink-soft transition-all flex items-center justify-center gap-2 disabled:opacity-60 shadow-lg shadow-ink/20"
            >
              {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <>Masuk ke Arsip <ArrowRight className="w-4 h-4 group-hover:translate-x-0.5 transition-transform" /></>}
            </button>
          </form>
        </div>
      </div>
    </div>
  );
}
